import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { devWarn } from "@/lib/debug";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { UploadSimple, Gear, Trash, Buildings, FloppyDisk, Hash, Wrench } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import MfaPanel from "@/components/settings/MfaPanel";

function CompanyLogo({ path, refreshKey }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    setSrc(null);
    if (!path) return;
    api.get(`/files/${path}`, { responseType: "blob" })
      .then((r) => setSrc(URL.createObjectURL(r.data)))
      .catch(() => {});
  }, [path, refreshKey]);
  if (!path) {
    return (
      <div className="w-24 h-24 border-2 border-primary rounded-full bg-white flex items-center justify-center">
        <span className="font-black text-primary text-lg">LOGO</span>
      </div>
    );
  }
  if (!src) return <div className="w-24 h-24 bg-secondary animate-pulse" />;
  return <img src={src} alt="logo" className="w-24 h-24 object-contain bg-white border border-border" />;
}

export default function Settings() {
  const { user } = useAuth();
  const isFullAdmin = user?.role === "admin";
  const [company, setCompany] = useState({ name: "", tagline: "", address: "", initials: "", kepada_yth_default: "", logo_path: null });
  const [savingCompany, setSavingCompany] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoKey, setLogoKey] = useState(0);
  const [counterPreview, setCounterPreview] = useState(null); // { year, seq, next_no_spb }
  const [counterInput, setCounterInput] = useState("");
  const [savingCounter, setSavingCounter] = useState(false);
  // Maintenance-mode toggle (full-admin only). Shows an "Under Upgrade"
  // full-screen page to every non-admin user across the app when enabled.
  // `duration_min` is a *client-side* helper — when the admin clicks
  // Aktifkan we translate it into an ISO end_at posted to the backend.
  const [maint, setMaint] = useState({ enabled: false, message: "", end_at: null });
  const [maintDuration, setMaintDuration] = useState("10");
  const [savingMaint, setSavingMaint] = useState(false);

  const loadCounter = async () => {
    try {
      const r = await api.get("/spb/next-number");
      setCounterPreview(r.data);
    } catch (err) {
      // Non-fatal — the counter card just won't show a preview.
      // Log for diagnostics but don't disturb the user (Settings page still works).
      devWarn("[Settings] Failed to fetch SPB next-number preview:", err);
    }
  };

  const load = async () => {
    const { data } = await api.get("/settings/company");
    setCompany(data);
    await loadCounter();
    if (isFullAdmin) {
      try {
        const r = await api.get("/settings/maintenance");
        setMaint({
          enabled: !!r.data.enabled,
          message: r.data.message || "",
          end_at: r.data.end_at || null,
        });
      } catch (err) {
        devWarn("[Settings] Failed to load maintenance flag:", err);
      }
    }
  };
  useEffect(() => {
    load();
    // Bootstrap-once — `load` is a stable closure that fires exactly on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSaveCounter = async (e) => {
    e.preventDefault();
    const n = parseInt(counterInput, 10);
    if (!Number.isFinite(n) || n < 1) {
      toast.error("Masukkan angka bulat ≥ 1");
      return;
    }
    setSavingCounter(true);
    try {
      const r = await api.put("/spb/counter", { start_from: n });
      toast.success(`Counter di-set. SPB berikutnya: ${r.data.next_no_spb}`);
      setCounterInput("");
      await loadCounter();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingCounter(false);
    }
  };

  const onSaveCompany = async (e) => {
    e.preventDefault();
    setSavingCompany(true);
    try {
      const { data } = await api.post("/settings/company", {
        name: company.name,
        tagline: company.tagline,
        address: company.address,
        initials: company.initials,
        kepada_yth_default: company.kepada_yth_default,
      });
      setCompany(data);
      toast.success("Data perusahaan disimpan");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingCompany(false); }
  };

  const onUploadCompanyLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/settings/company/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCompany((c) => ({ ...c, logo_path: data.logo_path }));
      setLogoKey((k) => k + 1);
      toast.success("Logo perusahaan diupload");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadingLogo(false); e.target.value = ""; }
  };

  const onDeleteCompanyLogo = async () => {
    if (!window.confirm("Hapus logo perusahaan?")) return;
    try {
      await api.delete("/settings/company/logo");
      setCompany((c) => ({ ...c, logo_path: null }));
      setLogoKey((k) => k + 1);
      toast.success("Logo dihapus. Monogram default akan dipakai.");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onSaveMaintenance = async (enabled) => {
    setSavingMaint(true);
    try {
      // Translate duration (minutes) → ISO end_at only when turning on.
      let end_at = null;
      if (enabled) {
        const mins = parseInt(maintDuration, 10);
        if (Number.isFinite(mins) && mins > 0) {
          end_at = new Date(Date.now() + mins * 60 * 1000).toISOString();
        }
      }
      await api.post("/settings/maintenance", { enabled, message: maint.message, end_at });
      setMaint((m) => ({ ...m, enabled, end_at }));
      toast.success(enabled
        ? `Mode maintenance AKTIF${end_at ? ` — estimasi ${maintDuration} menit` : ""}. User non-admin akan lihat halaman upgrade.`
        : "Mode maintenance NONAKTIF — app kembali normal.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingMaint(false);
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-4xl">
      <div className="border-b border-border pb-6 mb-6">
        <div className="overline">Konfigurasi Sistem</div>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2 flex items-center gap-3">
          <span className="w-11 h-11 rounded-full bg-orange-100 text-primary flex items-center justify-center shrink-0">
            <Gear size={22} weight="bold" />
          </span>
          Setelan
        </h1>
      </div>

      {/* Maintenance Mode — full-admin only. Enabling this shows a
          full-screen "Sedang Di-Upgrade" page to every non-admin user across
          the app so they know to wait during a deployment or migration. */}
      {isFullAdmin && (
        <div
          className={`border-2 p-6 mb-8 ${maint.enabled ? "border-orange-500 bg-orange-50" : "border-border bg-card"}`}
          data-testid="maintenance-toggle-card"
        >
          <div className="overline mb-2">00 · Mode Upgrade</div>
          <h2 className="font-bold text-lg tracking-tight mb-2 flex items-center gap-2">
            <Wrench size={22} weight="bold" /> Mode Sedang Di-Upgrade
            {maint.enabled && (
              <span
                className="ml-2 text-[10px] font-black uppercase tracking-widest px-2 py-1 bg-orange-500 text-white"
                data-testid="maintenance-active-badge"
              >
                AKTIF
              </span>
            )}
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            Saat aktif, semua user (kecuali admin) akan diarahkan ke halaman{" "}
            <strong>Sedang Di-Upgrade</strong>. Admin tetap bisa akses supaya bisa mematikan mode ini setelah selesai.
          </p>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <Label className="overline block mb-1" htmlFor="maint-message">
                  Pesan Kustom (opsional)
                </Label>
                <Textarea
                  id="maint-message"
                  value={maint.message}
                  onChange={(e) => setMaint((m) => ({ ...m, message: e.target.value }))}
                  placeholder="cth: Aplikasi sedang update ke versi 2.5."
                  className="rounded-xl font-mono text-sm min-h-[70px]"
                  data-testid="maintenance-message-input"
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  Kalau kosong, halaman upgrade pakai pesan default.
                </p>
              </div>
              <div>
                <Label className="overline block mb-1" htmlFor="maint-duration">
                  Estimasi (menit)
                </Label>
                <Input
                  id="maint-duration"
                  type="number"
                  min="1"
                  max="240"
                  value={maintDuration}
                  onChange={(e) => setMaintDuration(e.target.value)}
                  placeholder="10"
                  className="rounded-xl font-mono h-11"
                  data-testid="maintenance-duration-input"
                  disabled={maint.enabled}
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  Countdown akan tampil di halaman upgrade.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 items-center">
              {!maint.enabled ? (
                <Button
                  type="button"
                  onClick={() => onSaveMaintenance(true)}
                  disabled={savingMaint}
                  className="rounded-xl h-11 font-bold uppercase tracking-wider bg-orange-600 hover:bg-orange-700 text-white"
                  data-testid="maintenance-enable-button"
                >
                  <Wrench size={16} className="mr-2" />
                  {savingMaint ? "Menyimpan…" : "Aktifkan Mode Upgrade"}
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={() => onSaveMaintenance(false)}
                  disabled={savingMaint}
                  className="rounded-xl h-11 font-bold uppercase tracking-wider bg-emerald-700 hover:bg-emerald-800 text-white"
                  data-testid="maintenance-disable-button"
                >
                  {savingMaint ? "Menyimpan…" : "Matikan — Buka Kembali App"}
                </Button>
              )}
              <a
                href="/maintenance"
                target="_blank"
                rel="noreferrer"
                className="text-xs underline text-muted-foreground hover:text-foreground"
                data-testid="maintenance-preview-link"
              >
                Preview halaman ↗
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Company Profile */}
      <div className="border border-border bg-card p-6 mb-8">
        <div className="overline mb-2">01 · Profil Perusahaan</div>
        <h2 className="font-bold text-lg tracking-tight mb-2 flex items-center gap-2">
          <Buildings size={22} weight="bold" /> Data Perusahaan
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          Data ini akan muncul di header SPB (nama, tagline, alamat, logo). Kertas cetak: <strong>A5 Landscape</strong>.
        </p>

        <div className="flex flex-col md:flex-row gap-6 mb-6">
          <div className="flex flex-col items-center gap-3">
            <CompanyLogo path={company.logo_path} refreshKey={logoKey} />
            <div className="flex gap-2">
              <label className="cursor-pointer">
                <input type="file" accept="image/*" onChange={onUploadCompanyLogo} className="hidden" data-testid="upload-company-logo-input" />
                <span className={`inline-flex items-center gap-1 px-3 py-2 border text-xs font-bold uppercase tracking-wider ${
                  uploadingLogo ? "bg-secondary" : "bg-primary text-primary-foreground border-primary hover:bg-primary/90"
                }`}>
                  <UploadSimple size={14} weight="bold" />
                  {uploadingLogo ? "..." : "Upload"}
                </span>
              </label>
              {company.logo_path && (
                <Button size="sm" variant="outline" className="rounded-xl text-destructive" onClick={onDeleteCompanyLogo} data-testid="delete-company-logo-button">
                  <Trash size={14} />
                </Button>
              )}
            </div>
          </div>

          <form onSubmit={onSaveCompany} className="flex-1 space-y-4" data-testid="company-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="overline block mb-2">Nama Perusahaan *</Label>
                <Input value={company.name || ""} onChange={(e) => setCompany({ ...company, name: e.target.value })} required className="rounded-xl h-11 font-bold" data-testid="input-company-name" />
              </div>
              <div>
                <Label className="overline block mb-2">Inisial (Kode SPB)</Label>
                <Input value={company.initials || ""} onChange={(e) => setCompany({ ...company, initials: e.target.value.toUpperCase() })} maxLength={5} className="rounded-xl h-11 font-mono font-bold" placeholder="SBJ" data-testid="input-company-initials" />
              </div>
            </div>
            <div>
              <Label className="overline block mb-2">Tagline</Label>
              <Input value={company.tagline || ""} onChange={(e) => setCompany({ ...company, tagline: e.target.value })} className="rounded-xl h-11" data-testid="input-company-tagline" />
            </div>
            <div>
              <Label className="overline block mb-2">Alamat</Label>
              <Textarea value={company.address || ""} onChange={(e) => setCompany({ ...company, address: e.target.value })} className="rounded-xl" rows={2} data-testid="input-company-address" />
            </div>
            <div>
              <Label className="overline block mb-2">Kepada Yth (Default)</Label>
              <Input value={company.kepada_yth_default || ""} onChange={(e) => setCompany({ ...company, kepada_yth_default: e.target.value })} className="rounded-xl h-11" data-testid="input-kepada-yth" />
            </div>
            <Button type="submit" disabled={savingCompany} className="rounded-xl font-bold uppercase tracking-wider" data-testid="save-company-button">
              <FloppyDisk size={16} className="mr-2" />
              {savingCompany ? "Menyimpan..." : "Simpan Data Perusahaan"}
            </Button>
          </form>
        </div>
      </div>

      {/* SPB Counter — full admin only */}
      {isFullAdmin && (
        <div className="border border-border bg-card p-6 mb-8" data-testid="spb-counter-card">
          <div className="overline mb-2">02 · Counter Nomor SPB</div>
          <h2 className="font-bold text-lg tracking-tight mb-2 flex items-center gap-2">
            <Hash size={22} weight="bold" /> No Awal SPB
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            Set nomor awal SPB untuk tahun berjalan. Format tetap <code className="font-mono">YY-NNNNN</code>.
            Nomor mulai <strong>harus lebih besar</strong> dari counter saat ini agar tidak duplikat.
          </p>

          {counterPreview && (
            <div className="mb-4 flex flex-wrap items-center gap-3 border border-border bg-secondary/30 p-3">
              <div>
                <div className="overline text-[10px]">Tahun</div>
                <div className="font-mono font-bold text-lg" data-testid="counter-year">{counterPreview.year}</div>
              </div>
              <div className="w-px h-8 bg-border" />
              <div>
                <div className="overline text-[10px]">Counter Saat Ini</div>
                <div className="font-mono font-bold text-lg" data-testid="counter-current">{counterPreview.seq - 1}</div>
              </div>
              <div className="w-px h-8 bg-border" />
              <div>
                <div className="overline text-[10px]">SPB Berikutnya</div>
                <div className="font-mono font-black text-lg text-primary" data-testid="counter-next-preview">
                  {counterPreview.next_no_spb}
                </div>
              </div>
            </div>
          )}

          <form onSubmit={onSaveCounter} className="flex flex-wrap items-end gap-3" data-testid="counter-form">
            <div className="flex-1 min-w-[180px]">
              <Label className="overline block mb-2">Mulai dari nomor</Label>
              <Input
                type="number"
                min={1}
                step={1}
                value={counterInput}
                onChange={(e) => setCounterInput(e.target.value)}
                placeholder="cth: 1000"
                className="rounded-xl h-11 font-mono"
                data-testid="counter-input"
              />
            </div>
            <Button
              type="submit"
              disabled={savingCounter || !counterInput}
              className="rounded-xl h-11 font-bold uppercase tracking-wider"
              data-testid="counter-save-button"
            >
              <FloppyDisk size={16} className="mr-2" />
              {savingCounter ? "Menyimpan..." : "Set Counter"}
            </Button>
          </form>
        </div>
      )}

      {/* Template PDF block moved to Layout Editor (`/layout-editor`) so
          admins can preview + upload templates in the same place. Removed
          from Settings to avoid two places to manage the same thing. */}

      {/* MFA panel — self-hides for non-full-admin roles via a /status probe. */}
      {isFullAdmin && <MfaPanel />}

      {/* Users management moved to /users (Master User) page.
          Admins add / manage users there — Settings page focuses on
          template + company only. */}
    </div>
  );
}
